<template>
  <v-dialog
    v-model="dialogVisible"
    persistent
    max-width="600"
    @keydown.esc="dismiss"
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white" large>mdi-bell-ring</v-icon>
        <span class="text-h5">Field Order Updates</span>
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" small>
          {{ fjo_notifications.length }}
          {{ fjo_notifications.length === 1 ? "update" : "updates" }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4" style="max-height: 500px; overflow-y: auto">
        <!-- New Orders -->
        <div v-if="newOrders.length > 0" class="mb-4">
          <div class="subtitle-2 green--text text--darken-2 mb-2">
            <v-icon small color="green darken-2">mdi-plus-circle</v-icon>
            New Orders ({{ newOrders.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="order in newOrders"
              :key="'new-' + order.id"
              class="green lighten-5 mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon color="green darken-2">mdi-clipboard-plus</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ order.customer_name }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <v-chip x-small color="primary" dark class="mr-1">
                    {{ order.job_type }}
                  </v-chip>
                  <span class="mr-2">{{ order.order_source }}</span>
                  <span class="mr-2">
                    Scheduled: {{ formatDate(order.scheduled_date) }}
                  </span>
                  <span v-if="order.branch" class="grey--text">
                    {{ order.branch.branch_name }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption grey--text">
                  Inserted by: {{ order.inserted_by_hats }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Updated Orders -->
        <div v-if="updatedOrders.length > 0" class="mb-4">
          <div class="subtitle-2 orange--text text--darken-2 mb-2">
            <v-icon small color="orange darken-2">mdi-pencil-circle</v-icon>
            Updated Orders ({{ updatedOrders.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="order in updatedOrders"
              :key="'updated-' + order.id"
              class="orange lighten-5 mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon color="orange darken-2">mdi-clipboard-edit</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">
                  {{ order.customer_name }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <v-chip x-small color="primary" dark class="mr-1">
                    {{ order.job_type }}
                  </v-chip>
                  <span class="mr-2">{{ order.order_source }}</span>
                  <span class="mr-2">
                    Scheduled: {{ formatDate(order.scheduled_date) }}
                  </span>
                  <span v-if="order.branch" class="grey--text">
                    {{ order.branch.branch_name }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption grey--text">
                  Inserted by: {{ order.inserted_by_hats }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Cancelled Orders -->
        <div v-if="cancelledOrders.length > 0" class="mb-4">
          <div class="subtitle-2 red--text text--darken-2 mb-2">
            <v-icon small color="red darken-2">mdi-cancel</v-icon>
            Cancelled Orders ({{ cancelledOrders.length }})
          </div>
          <v-list dense>
            <v-list-item
              v-for="order in cancelledOrders"
              :key="'cancelled-' + order.id"
              class="red lighten-5 mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon color="red">mdi-clipboard-remove</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title
                  class="font-weight-bold text-decoration-line-through"
                >
                  {{ order.customer_name }}
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  <v-chip x-small color="primary" dark class="mr-1">
                    {{ order.job_type }}
                  </v-chip>
                  <span class="mr-2">{{ order.order_source }}</span>
                  <span class="mr-2">
                    Scheduled: {{ formatDate(order.scheduled_date) }}
                  </span>
                  <span v-if="order.branch" class="grey--text">
                    {{ order.branch.branch_name }}
                  </span>
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption grey--text">
                  Inserted by: {{ order.inserted_by_hats }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Empty state -->
        <div v-if="fjo_notifications.length === 0" class="text-center pa-6">
          <v-icon large color="grey lighten-1" class="mb-3"
            >mdi-bell-check-outline</v-icon
          >
          <div class="grey--text">No new notifications.</div>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" dark @click="dismiss">
          <v-icon left small>{{
            fjo_notifications.length > 0 ? "mdi-check" : "mdi-close"
          }}</v-icon>
          {{ fjo_notifications.length > 0 ? "Dismiss" : "Close" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "FieldJobOrderNotificationDialog",
  computed: {
    ...mapGetters({
      fjo_notifications: "field_job_order/fjo_notifications",
      show_notification_dialog: "field_job_order/show_notification_dialog",
    }),
    dialogVisible: {
      get() {
        return this.show_notification_dialog;
      },
      set() {
        // Controlled entirely by store; prevent direct set
      },
    },
    newOrders() {
      return this.fjo_notifications.filter(
        (o) => o.notification_type === "new"
      );
    },
    updatedOrders() {
      return this.fjo_notifications.filter(
        (o) => o.notification_type === "updated"
      );
    },
    cancelledOrders() {
      return this.fjo_notifications.filter(
        (o) => o.notification_type === "cancelled"
      );
    },
  },
  watch: {
    show_notification_dialog(val) {
      if (val && this.fjo_notifications.length > 0) {
        this.playNotificationSound();
      }
    },
  },
  methods: {
    ...mapActions({
      dismiss_fjo_notifications: "field_job_order/dismiss_fjo_notifications",
    }),
    dismiss() {
      this.dismiss_fjo_notifications();
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    },
    /**
     * Play a notification sound using the Web Audio API.
     * This works reliably in both Electron and standard browsers
     * without requiring an external audio file.
     */
    playNotificationSound() {
      try {
        var AudioContextClass =
          window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        var ctx = new AudioContextClass();

        // First tone: a pleasant notification chime (E5 = 659 Hz)
        var osc1 = ctx.createOscillator();
        var gain1 = ctx.createGain();
        osc1.type = "sine";
        osc1.frequency.setValueAtTime(659, ctx.currentTime);
        gain1.gain.setValueAtTime(0.3, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.3);

        // Second tone: higher chime (A5 = 880 Hz) after a short delay
        var osc2 = ctx.createOscillator();
        var gain2 = ctx.createGain();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.15);
        gain2.gain.setValueAtTime(0.01, ctx.currentTime);
        gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(ctx.currentTime + 0.15);
        osc2.stop(ctx.currentTime + 0.5);

        // Third tone: resolve (C#6 = 1109 Hz)
        var osc3 = ctx.createOscillator();
        var gain3 = ctx.createGain();
        osc3.type = "sine";
        osc3.frequency.setValueAtTime(1109, ctx.currentTime + 0.3);
        gain3.gain.setValueAtTime(0.01, ctx.currentTime);
        gain3.gain.setValueAtTime(0.25, ctx.currentTime + 0.3);
        gain3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.7);
        osc3.connect(gain3);
        gain3.connect(ctx.destination);
        osc3.start(ctx.currentTime + 0.3);
        osc3.stop(ctx.currentTime + 0.7);

        // Clean up context after sound finishes
        setTimeout(function () {
          ctx.close();
        }, 1000);
      } catch (e) {
        // Audio API not available or blocked; fail silently
        console.warn("[FieldJobOrderNotification] Could not play sound:", e);
      }
    },
  },
};
</script>
